import { assign, setup } from 'xstate';

/**
 * F-017 `authoringQueueItemMachine` (§XI — data-model §2.2). Owns the
 * lifecycle of one Authoring Queue Item — the honestly-labelled home for a
 * study-specific requirement awaiting human authorship. The server is the
 * transition authority (restore → transition → persist; audited).
 *
 * `authored` is terminal and links the realised novel TestCase id — AUTHOR
 * composes with the EXISTING CreateTestCase path (origin novel, NOV- ids);
 * no parallel creation mechanism exists (R9). DISMISS requires a reason.
 * Wire statuses equal the state node ids verbatim (all single words — the
 * snake_case/camelCase mapping question does not arise here).
 */
export type AuthoringQueueItemContext = {
  readonly authoredTestCaseId: string | null;
};

export type AuthoringQueueItemEvent =
  | { readonly type: 'START_DRAFT' }
  | { readonly type: 'AUTHOR'; readonly testCaseId: string }
  | { readonly type: 'ABANDON' }
  | { readonly type: 'DISMISS'; readonly reason: string }
  | { readonly type: 'RESTORE' };

export const authoringQueueItemMachine = setup({
  types: {} as {
    context: AuthoringQueueItemContext;
    events: AuthoringQueueItemEvent;
  },
  guards: {
    hasReason: ({ event }) => event.type === 'DISMISS' && event.reason.trim().length > 0,
  },
}).createMachine({
  id: 'authoringQueueItem',
  initial: 'queued',
  context: { authoredTestCaseId: null },
  states: {
    queued: {
      on: {
        START_DRAFT: { target: 'drafting' },
        DISMISS: { target: 'dismissed', guard: 'hasReason' },
      },
    },
    drafting: {
      on: {
        AUTHOR: {
          target: 'authored',
          actions: assign({
            authoredTestCaseId: ({ event }) => (event.type === 'AUTHOR' ? event.testCaseId : null),
          }),
        },
        ABANDON: { target: 'queued' },
        DISMISS: { target: 'dismissed', guard: 'hasReason' },
      },
    },
    authored: { type: 'final' },
    dismissed: {
      on: {
        RESTORE: { target: 'queued' },
      },
    },
  },
});

export const AUTHORING_QUEUE_ITEM_STATUSES = [
  'queued',
  'drafting',
  'authored',
  'dismissed',
] as const;

export type AuthoringQueueItemStatus = (typeof AUTHORING_QUEUE_ITEM_STATUSES)[number];

export type AuthoringQueueItemState = AuthoringQueueItemStatus;

export const isAuthoringQueueItemStatus = (raw: string): raw is AuthoringQueueItemStatus =>
  (AUTHORING_QUEUE_ITEM_STATUSES as readonly string[]).includes(raw);

export const parseAuthoringQueueItemStatus = (raw: string): AuthoringQueueItemStatus => {
  if (!isAuthoringQueueItemStatus(raw)) {
    throw new Error(`Invalid AuthoringQueueItemStatus: ${JSON.stringify(raw)}`);
  }
  return raw;
};
